export type RuntimeVariableMetadata = {
  purpose: string;
  importance: string;
  documented: boolean;
};

type MetadataCopy = readonly [purpose: string, importance: string];

/**
 * Operator-facing documentation for the live runtime inventory.
 *
 * This is descriptive UI metadata, not a configuration authority. Existing
 * environment-backed behavior remains legacy migration input; new behavior
 * belongs in the RDS control plane and signed runtime snapshots.
 *
 * Sources reviewed on 2026-07-14: the production (`stg`) Feature Flags page,
 * the workspace config references, and the owning bot/orchestrator/composer/
 * dashboard/mobile-BFF code paths.
 */
const METADATA_BY_NAME: Record<string, MetadataCopy> = {
  ACCESS_TTL_SEC: [
    "Sets how long a mobile API access token remains valid after login.",
    "A short lifetime limits exposure from a stolen token while still keeping normal app sessions usable.",
  ],
  AERIAL_INTRO_ENABLED: [
    "Allows eligible real-estate skill runs to add an aerial-style multi-reference opening shot for detected houses.",
    "It changes a high-visibility first shot and provider usage, so it needs a fast rollback independent of ordinary listings.",
  ],
  ALLOWED_ORIGINS: [
    "Lists browser origins allowed by the dashboard API CORS policy.",
    "It prevents unapproved websites from calling authenticated CRM endpoints from a browser.",
  ],
  ALLOWED_S3_KEY_PREFIXES: [
    "Restricts which object-key prefixes the dashboard API may read, sign, upload, or delete.",
    "This is a storage authorization boundary that limits accidental or malicious access outside Echoflicks media paths.",
  ],
  ANTHROPIC_API_KEY: [
    "Authenticates orchestrator calls to Anthropic models used by configured directing and narrative tasks.",
    "Without it, any workflow routed to Anthropic fails; exposure would permit unauthorized billable model use.",
  ],
  AT_LEAST_ONCE_ENABLED: [
    "Keeps an orchestrator queue message unacknowledged while an Echo runs so interrupted work can be redelivered.",
    "It reduces lost Echoes during deploys or crashes, but requires idempotent handling to avoid duplicate work.",
  ],
  AUTONOMOUS_SKILLS_ENABLED: [
    "Enables the orchestrator's autonomous skill system, including deterministic real-estate planning and skill-scoped creative overrides.",
    "It is the master behavior gate separating skill-driven B2B runs from the normal B2C interview flow.",
  ],
  AWS_ACCESS_KEY_ID: [
    "Supplies the access-key identifier for explicit AWS API credentials when workload identity is not used.",
    "It is security-sensitive and must match the secret key; incorrect credentials break queues, storage, or service discovery.",
  ],
  AWS_REGION: [
    "Selects the default AWS region for SDK clients and regional resources.",
    "A wrong region makes valid buckets, queues, tables, and services appear missing or routes calls to the wrong environment.",
  ],
  AWS_SECRET_ACCESS_KEY: [
    "Supplies the secret half of explicit AWS API credentials when workload identity is not used.",
    "Exposure grants the paired key's AWS permissions, while a mismatch breaks every client using those credentials.",
  ],
  AWS_SQS_REGION: [
    "Overrides the AWS region used specifically for SQS queue clients.",
    "Queues are regional, so this must agree with their URLs to prevent polling or publishing failures.",
  ],
  BOT_DESCRIPTION: [
    "Provides the configured descriptive text for the WhatsApp bot identity and related registration surfaces.",
    "Consistent identity copy helps operators verify that a deployment represents the intended Felix experience.",
  ],
  BOT_NAME: [
    "Provides the configured display name for the WhatsApp bot.",
    "It distinguishes the customer-facing bot identity across environments and operator tooling.",
  ],
  BOT_RESULTS_QUEUE_URL: [
    "Points dashboard/remake processing at the queue used to return completed video results to the bot flow.",
    "Using the wrong queue can strand completed Echoes or deliver results into another environment.",
  ],
  BOUNDARY_QC_ENABLED: [
    "Runs an authoritative scan of the final video for one-frame flashbacks or visual discontinuities at scene cuts.",
    "It catches concat/render seam regressions that are easy to miss but highly visible in the delivered Echo.",
  ],
  BOUNDARY_QC_FAIL_CLOSED: [
    "Controls whether a failed final boundary-QC check stops delivery instead of only reporting the problem.",
    "It determines the safety-versus-availability tradeoff when a render may contain visible cut artifacts.",
  ],
  CLOUD_PROVIDER: [
    "Selects the AWS or GCP adapter family used for storage, queues, jobs, and other cloud integrations.",
    "It is a top-level routing decision; a mismatch can send data or work to the wrong cloud stack.",
  ],
  COMPOSER_BATCH_JOB_DEFINITION: [
    "Names the AWS Batch job definition the orchestrator uses to start a video-composer execution.",
    "It pins the container/runtime contract; a stale definition can run old code or omit required configuration.",
  ],
  COMPOSER_BATCH_JOB_QUEUE: [
    "Names the AWS Batch queue where the orchestrator submits composer jobs.",
    "The queue controls scheduling and compute placement, so a wrong value leaves Echoes waiting or sends them to the wrong fleet.",
  ],
  COMPOSER_JOB_DEFINITION: [
    "Names the composer Batch job definition used by dashboard-driven remake operations.",
    "Remakes must use the intended composer revision to reproduce current pipeline behavior safely.",
  ],
  COMPOSER_JOB_QUEUE: [
    "Names the Batch queue used for dashboard-driven composer or remake jobs.",
    "It determines where operator-triggered work is scheduled and prevents cross-environment dispatch.",
  ],
  COMPOSER_PREWARM_ENABLED: [
    "Starts the composer early so container startup overlaps the orchestrator's remaining script work, then waits for the script envelope.",
    "It reduces end-to-end latency while the normal post-script dispatch remains the delivery guarantee.",
  ],
  COMPOSE_CLAIM_ENABLED: [
    "Uses a durable per-workload claim so only one composer execution becomes the active render winner.",
    "It prevents duplicate prewarm and normal-dispatch jobs from both producing or delivering the same Echo.",
  ],
  CONFIG_INVENTORY_JSON: [
    "Defines the compute resources that the read-only runtime-flags endpoint introspects across ECS, Batch, and Lambda.",
    "This page is only trustworthy when the inventory covers every relevant service in the literal environment.",
  ],
  CONFIG_SIGNING_KEY_ID: [
    "Identifies the key used by the configuration control plane to sign immutable runtime snapshots.",
    "Services use the signature to reject tampered or cross-environment configuration artifacts.",
  ],
  CONFIG_SNAPSHOT_BUCKET: [
    "Names the environment-local S3 bucket that stores signed runtime-configuration snapshots.",
    "The bucket is the distribution layer for effective config and must remain isolated between production (`stg`) and engineering staging (`staging`).",
  ],
  CONFIG_SNAPSHOT_PREFIX: [
    "Scopes runtime-configuration snapshot objects to the current literal environment inside the snapshot bucket.",
    "The prefix prevents services from reading a release published for another environment.",
  ],
  CONFIG_SOUND_STATE_ENDPOINT: [
    "Points the dashboard API at the control-plane endpoint that reports the effective sound configuration state.",
    "It lets operators compare desired, published, and applied sound policy instead of relying on deploy-time assumptions.",
  ],
  CONSENT_NUDGE_ENABLED: [
    "Enables the WhatsApp follow-up that reminds users to complete required consent choices.",
    "It affects GDPR-facing onboarding and must not pressure users after consent is already resolved.",
  ],
  CREATIVE_HARNESS_MODE: [
    "Selects whether the orchestrator's Creative Director Harness is off, observing, or actively influencing the plan.",
    "The mode changes how semi-autonomous creative decisions reach customer videos and provides a rollback boundary.",
  ],
  DASHBOARD_API_BASE_URL: [
    "Points the mobile BFF at the upstream dashboard API used for user-scoped Echo reads and playback requests.",
    "A correct environment-local endpoint is essential to prevent data lookup failures or cross-environment access.",
  ],
  DASHBOARD_API_SECRET: [
    "Authenticates trusted server-to-server calls to the dashboard API.",
    "It protects operator and user data routes; exposure could bypass the browser-facing authentication boundary.",
  ],
  DASHBOARD_EVENT_BUS_NAME: [
    "Names the EventBridge bus that receives journey, QC, cost, and generated-asset events from pipeline services.",
    "If it is wrong or absent, CRM audit history becomes incomplete even when video generation itself succeeds.",
  ],
  DASHBOARD_EVENT_SOURCE: [
    "Sets the EventBridge source identifier attached to events emitted by a service.",
    "Stable source names let the indexer route, audit, and distinguish bot, orchestrator, and composer events.",
  ],
  DATABASE_URL: [
    "Provides the WhatsApp bot's PostgreSQL connection string.",
    "It is both a critical availability dependency and a secret containing database location and credentials.",
  ],
  DB_PASSWORD: [
    "Provides a database password used by deployment or compose wiring to build a database connection.",
    "Exposure compromises the database account, while drift from the connection string prevents startup or migrations.",
  ],
  DEBUG_MODE_ALLOWED_USERS: [
    "Lists WhatsApp user IDs allowed to access the bot's debug menu and automated debug flow.",
    "The allowlist keeps internal diagnostics and automation away from ordinary customers.",
  ],
  DEBUG_MODE_ENABLED: [
    "Enables the WhatsApp bot's internal debug menu and related debug-only interactions.",
    "It is operationally powerful and must remain bounded by an allowlist to avoid exposing internal controls to customers.",
  ],
  DEFAULT_PHONE_REGION: [
    "Sets the fallback country used by the mobile BFF when normalizing phone numbers that omit a country code.",
    "Phone normalization defines user identity, so a wrong region can map a login to the wrong WhatsApp number.",
  ],
  DEV_MODE: [
    "Allows the orchestrator to consume development-prefixed queue message types and development behavior.",
    "It must stay controlled because dev messages may bypass assumptions used by the normal production flow.",
  ],
  DURATION_QC_ENABLED: [
    "Checks that the delivered video's duration matches the timeline contract within the configured frame tolerance.",
    "It catches missing, duplicated, or mistimed material before an Echo is treated as complete.",
  ],
  DURATION_QC_FAIL_CLOSED: [
    "Controls whether a duration mismatch fails the composer job instead of producing a warning.",
    "It sets the delivery-versus-correctness policy for a video that violates its planned timeline.",
  ],
  ELEVENLABS_API_KEY: [
    "Authenticates ElevenLabs requests for voice, text-to-speech, and music operations used by the pipeline.",
    "Missing credentials break audio generation; exposed credentials permit unauthorized billable use.",
  ],
  ELEVENLABS_VOICE_READ_SECRET_ARN: [
    "Points the dashboard API at the secret containing credentials used to read ElevenLabs voice metadata.",
    "It enables operator voice audit data without exposing the credential to the browser.",
  ],
  EMAIL_ENABLED: [
    "Enables the bot's optional SMTP email-delivery path.",
    "It prevents accidental outbound email when SMTP identity or consent requirements are not ready.",
  ],
  EMAIL_FROM: [
    "Sets the sender address used for bot-generated email.",
    "A valid, authorized sender protects deliverability and makes the message's origin clear to recipients.",
  ],
  ENVIRONMENT: [
    "Identifies the literal deployment environment consumed by service guards, resource naming, telemetry, and config validation.",
    "Echoflicks naming is safety-critical: production is `stg`, while engineering staging is `staging`.",
  ],
  EVENTS_TABLE_NAME: [
    "Names the DynamoDB table that stores detailed dashboard journey and audit events.",
    "The correct environment-local table preserves the CRM creation journey without mixing customer data across environments.",
  ],
  FACEBOOK_APP_ID: [
    "Identifies the Meta application associated with the WhatsApp integration.",
    "It ties webhook and Graph API operations to the correct Meta app and environment.",
  ],
  FACEBOOK_APP_SECRET: [
    "Authenticates privileged Meta application operations such as callback registration.",
    "Exposure can compromise the Meta app; an incorrect secret prevents app-level operations and verification.",
  ],
  FACE_GEMINI_ARBITER_ENABLED: [
    "Adds a Gemini semantic pass to reject false face detections and recover profile or occluded faces for smart cropping.",
    "It improves subject preservation but adds model cost and latency, so it needs an independent rollback.",
  ],
  FACE_RECALL_BOOST_ENABLED: [
    "Uses a local multi-detector face ensemble to improve recall before crop planning.",
    "Better recall reduces accidental face clipping while keeping the common path deterministic and local.",
  ],
  FEATURE_COHORTS_BOOTSTRAP_ADMINS: [
    "Lists initial administrators allowed to manage feature cohorts before normal cohort permissions are available.",
    "It is a privileged bootstrap boundary and should remain minimal to prevent unauthorized rollout changes.",
  ],
  FEATURE_COHORTS_ENABLED: [
    "Enables cohort-based product behavior in the WhatsApp bot, including agency and controlled feature eligibility.",
    "It is the legacy gate separating selected B2B/B2C users from the default experience during migration to the RDS control plane.",
  ],
  FEATURE_COHORTS_ENVIRONMENT: [
    "Selects the cohort namespace the bot reads from the legacy cohort store.",
    "The namespace must match the product environment so membership cannot leak between production and test users.",
  ],
  FRAMING_DIAGNOSTIC_ASSETS_ENABLED: [
    "Persists controlled framing contact sheets and related visual evidence for operator diagnosis.",
    "The assets make crop failures explainable but increase storage and privacy exposure, so they stay explicitly gated.",
  ],
  FRAMING_PLAN_ENABLED: [
    "Builds coordinate-aware per-scene framing plans from subject intent, detections, and delivery aspect.",
    "It is the foundation for consistent subject-preserving crops across generation and final composition.",
  ],
  FRAMING_QC_ENABLED: [
    "Evaluates final pixels against the framing plan to detect clipped or lost required subjects.",
    "It turns crop quality from an implicit visual guess into an auditable delivery check.",
  ],
  FRAMING_QC_ENFORCEMENT_MODE: [
    "Selects whether framing QC is off, observe-only, allowed to repair, or allowed to reject and retry.",
    "The mode controls customer-impacting enforcement and lets operators stage the rollout before failing jobs.",
  ],
  FRAMING_QC_REPAIR_ENABLED: [
    "Allows framing QC to apply a bounded repair when the final crop violates the plan.",
    "It can rescue visible subject clipping, but bounded retries prevent repair loops or unplanned render cost.",
  ],
  GCP_PROJECT_ID: [
    "Identifies the GCP project used by Vertex, storage, or other configured Google Cloud integrations.",
    "It determines billing and resource isolation; a wrong project can fail access or cross an environment boundary.",
  ],
  GEMINI_API_KEY: [
    "Authenticates Gemini Developer API calls used for media analysis, directing, and quality checks.",
    "Missing credentials disable those model-backed stages; exposure permits unauthorized billable use.",
  ],
  GENERATED_CLIP_ANALYSIS_ENABLED: [
    "Re-analyzes generated video frames so framing plans can account for subject motion after image-to-video generation.",
    "Source-image framing alone cannot see provider-created motion, so this closes an important final-crop blind spot.",
  ],
  GENERATED_CLIP_GROUNDING_MAX_FRAMES: [
    "Caps how many frames from each generated clip may be sent through subject grounding.",
    "The cap bounds latency, model load, and exposure while still sampling motion across the clip.",
  ],
  GRACEFUL_DRAIN_ENABLED: [
    "Stops the orchestrator from pulling new messages on shutdown while allowing in-flight Echoes a grace period to finish.",
    "It reduces work loss and duplicate processing during deploys or scale-in events.",
  ],
  I2V_EXPLICIT_PROVIDER: [
    "Legacy compatibility override that forces a specific image-to-video provider for explicitly routed scenes.",
    "It can bypass normal tier routing, so drift from the frozen route contract changes cost, capability, and output quality.",
  ],
  I2V_GENERATION_QC_DETERMINISTIC_ENABLED: [
    "Runs deterministic checks such as decodability, duration, sample-frame validity, and minimum motion on generated clips.",
    "These low-variance checks catch broken provider outputs before subjective semantic review.",
  ],
  I2V_GENERATION_QC_ENABLED: [
    "Enables the post-generation quality gate for image-to-video clips before they enter final composition.",
    "It prevents corrupt, static, or semantically wrong clips from silently degrading the delivered Echo.",
  ],
  I2V_GENERATION_QC_ENFORCEMENT_MODE: [
    "Selects whether I2V generation QC is disabled, observe-only, retry/reject, or fail-open after an error.",
    "It controls the availability-versus-quality decision and the amount of provider retry cost.",
  ],
  I2V_GENERATION_QC_PROMPT_CORRECTION_ENABLED: [
    "Allows QC findings to produce a corrected prompt for a bounded I2V regeneration attempt.",
    "It turns diagnosed semantic failures into targeted repairs instead of blindly repeating the same provider request.",
  ],
  I2V_GENERATION_QC_SEMANTIC_ENABLED: [
    "Uses a vision model to judge identity, subject, expression, and prompt adherence in generated clips.",
    "It detects failures that duration and motion metrics cannot see, with added model cost and latency.",
  ],
  I2V_GENERATION_QC_SEMANTIC_ENFORCEMENT_MODE: [
    "Controls whether semantic I2V findings only observe, trigger retry/rejection, or fail open on evaluation errors.",
    "It lets Echoflicks tune strictness separately from deterministic media validation.",
  ],
  I2V_INPUT_FRAMING_FACE_AWARE_ENABLED: [
    "Uses detected face bounds when pre-cropping source images for image-to-video providers.",
    "It prevents provider inputs from clipping heads or faces before generation even starts.",
  ],
  I2V_LIGHT_PROVIDER: [
    "Legacy route selecting the lower-cost image-to-video provider tier for bulk or light-animation scenes.",
    "It materially affects per-Echo cost, throughput, and visual quality and must agree with the route contract.",
  ],
  I2V_PRIMARY_PROVIDER: [
    "Selects the primary image-to-video provider route used for hero or priority scenes.",
    "Hero-scene routing has an outsized effect on quality, capability, latency, and cost.",
  ],
  I2V_PROMPT_AUDIT_EVENTS_ENABLED: [
    "Emits attempt-level events with the resolved provider prompt, route, motion decision, QC context, and terminal status.",
    "It gives the CRM evidence of what was actually submitted without changing generated media.",
  ],
  I2V_PROMPT_DIRECTOR_ENABLED: [
    "Enables the orchestrator's per-image prompt-authoring pass for B2C image-to-video scenes.",
    "It can improve scene-specific motion but changes prompts, adds LLM cost, and must fail open to the legacy prompt path.",
  ],
  I2V_PROMPT_DIRECTOR_INCLUDE_IMAGES: [
    "Attaches each source photo to prompt-director model calls instead of authoring from text metadata alone.",
    "Multimodal context improves grounded prompts but increases request size, privacy exposure, and latency.",
  ],
  I2V_PROMPT_DIRECTOR_MISSING_IMAGE_POLICY: [
    "Defines whether prompt authoring warns, falls back, or blocks when a required source image cannot be loaded.",
    "The policy prevents a transient media problem from silently producing ungrounded prompts or unnecessarily blocking an Echo.",
  ],
  I2V_PROMPT_DIRECTOR_REAL_ESTATE_ENABLED: [
    "Allows prompt-director authoring on real-estate skill scenes instead of keeping deterministic skill prompts.",
    "B2B property videos have different accuracy and brand constraints, so this rollout is isolated from B2C.",
  ],
  I2V_PROMPT_DIRECTOR_REQUIRE_IMAGES: [
    "Requires source-image availability for prompt-director calls rather than permitting text-only degradation.",
    "It trades availability for visual grounding when unobserved image content would make a prompt unsafe or inaccurate.",
  ],
  I2V_PROVIDER: [
    "Legacy global image-to-video provider selector used when a more specific route does not override it.",
    "It is a broad compatibility fallback and can unexpectedly change many scenes if it drifts from primary/secondary routing.",
  ],
  I2V_PROVIDER_CIRCUIT_COOLDOWN_SECONDS: [
    "Sets how long an opened per-workload I2V provider circuit waits before allowing another attempt.",
    "It prevents a degraded provider from repeatedly stalling the Echo while still permitting recovery.",
  ],
  I2V_PROVIDER_CIRCUIT_FAILURE_THRESHOLD: [
    "Sets how many qualifying provider failures open the per-workload I2V circuit.",
    "A low threshold protects latency during outages; an overly low value can abandon a healthy provider after transient errors.",
  ],
  I2V_PROVIDER_FALLBACK_CHAIN: [
    "Defines the ordered provider routes attempted after the selected I2V provider cannot complete a clip.",
    "Fallback order determines resilience, cost, latency, and whether required scene capabilities remain available.",
  ],
  I2V_PROVIDER_MAX_CONCURRENCY_JSON: [
    "Overrides maximum in-flight clip generation per canonical I2V provider.",
    "It protects provider quotas and throughput; invalid aliases or excessive limits can cause throttling and cascading retries.",
  ],
  I2V_PROVIDER_PREFLIGHT_CACHE_SECONDS: [
    "Sets how long a provider readiness result is reused within a workload.",
    "Caching bounds health-check traffic while keeping capacity decisions fresh enough for routing.",
  ],
  I2V_PROVIDER_PREFLIGHT_ENABLED: [
    "Checks provider entitlement, credentials, capacity, and route readiness before submitting an I2V task.",
    "It avoids spending Echo latency on a route already known to be unavailable.",
  ],
  I2V_REGISTRY_DIGEST: [
    "Pins the expected digest of the canonical I2V provider and model registry.",
    "Digest agreement proves the running composer interprets provider IDs, capabilities, and pricing from the intended catalog.",
  ],
  I2V_ROUTE_CONTRACT_FILE: [
    "Points the composer at the deployed immutable I2V route-contract file.",
    "The contract freezes provider/model routing for reproducibility and prevents ad hoc environment drift.",
  ],
  I2V_ROUTE_CONTRACT_JSON: [
    "Carries the orchestrator's frozen I2V route contract, including primary/secondary routes and catalog digests.",
    "It keeps planning and composition on the same provider decision for the lifetime of an Echo.",
  ],
  I2V_ROUTE_CONTRACT_STRICT: [
    "Requires the I2V route contract to validate and match the expected environment and registry instead of silently falling back.",
    "Strict validation prevents a render from using unreviewed or incompatible routing.",
  ],
  I2V_SECONDARY_PROVIDER: [
    "Selects the image-to-video route used for non-hero prompted scenes.",
    "Secondary routing dominates volume and therefore strongly affects cost, concurrency, and overall visual consistency.",
  ],
  INACTIVITY_NUDGE_ENABLED: [
    "Runs the bot scheduler that sends a bounded re-engagement message after a user becomes inactive mid-flow.",
    "It can recover abandoned Echoes but must respect WhatsApp timing, consent, and anti-spam constraints.",
  ],
  INFLIGHT_GAUGE_ENABLED: [
    "Publishes the orchestrator's count of actively processing Echoes for capacity and rollout observability.",
    "The gauge helps operators distinguish genuine pipeline demand from stuck or drained workers.",
  ],
  INTAKE_PAUSE_ENABLED: [
    "Allows the orchestrator to stop accepting new work while continuing controlled handling of already in-flight Echoes.",
    "It is an operational safety control for incidents, maintenance, and graceful traffic draining.",
  ],
  JWT_SIGNING_SECRET: [
    "Signs and verifies mobile-BFF access and refresh JWTs.",
    "Exposure enables token forgery, while rotation without coordination invalidates every active mobile session.",
  ],
  KLING_ACCESS_KEY: [
    "Provides the public credential identifier for direct Kling API authentication.",
    "It must match the Kling secret key; incorrect or exposed credentials break or compromise direct provider access.",
  ],
  KLING_SECRET_KEY: [
    "Provides the secret credential used to sign direct Kling API requests.",
    "Exposure permits unauthorized generation and billing; a mismatch makes the direct route unavailable.",
  ],
  LABS_BUCKET_NAME: [
    "Names the private object bucket used for Echo Labs inputs, outputs, and controlled artifacts.",
    "It isolates experimental media from normal customer storage and supports retention enforcement.",
  ],
  LABS_ENABLED: [
    "Enables authenticated Echo Labs API routes and operator experiment workflows.",
    "Labs can invoke expensive or experimental media paths, so it must be independently controllable.",
  ],
  LABS_EXPERIMENTS_TABLE_NAME: [
    "Names the table storing Labs experiment definitions, arms, and audit metadata.",
    "It preserves reproducibility and separates experiment design from individual job execution.",
  ],
  LABS_READINESS_TABLE_NAME: [
    "Names the table storing provider and route readiness observations for Labs.",
    "Operators need current readiness evidence before launching experiments against costly external providers.",
  ],
  LABS_RETENTION_DAYS: [
    "Sets how long Labs jobs and associated experimental artifacts are retained.",
    "A bounded lifetime controls GDPR/storage exposure and prevents experimental data from accumulating indefinitely.",
  ],
  LABS_TABLE_NAME: [
    "Names the primary table for Labs job state and execution records.",
    "The correct environment-local table is required for status, audit, retries, and result lookup.",
  ],
  LAUNCHPAD_ENABLED: [
    "Enables the Launchpad web-onboarding intake and the EFL-code WhatsApp handoff journey.",
    "It is the master product gate for a distinct acquisition and media-seeding flow.",
  ],
  LAUNCHPAD_MAX_MEDIA: [
    "Caps how many media files a Launchpad session may attach before the WhatsApp handoff.",
    "It bounds portal upload abuse and keeps seeded Echo workloads within the pipeline's expected input size.",
  ],
  LAUNCHPAD_MIN_MEDIA: [
    "Sets the minimum number of uploaded media files required before a Launchpad session counts as upload-complete.",
    "It prevents near-empty sessions from reaching WhatsApp with too little material for a viable Echo.",
  ],
  LAUNCHPAD_MINT_HOURLY_CAP: [
    "Caps how many Launchpad EFL codes may be minted per hour.",
    "It limits scripted or abusive session creation without affecting legitimate ad-driven traffic.",
  ],
  LAUNCHPAD_PORTAL_API_KEY: [
    "Authenticates trusted Launchpad portal requests to the bot's session/intake API.",
    "It prevents unauthorized creation or mutation of Launchpad sessions and uploaded media.",
  ],
  LAUNCHPAD_PORTAL_ORIGIN: [
    "Sets the trusted public origin of the Launchpad portal used in links and request validation.",
    "It keeps users and cross-origin checks pointed at the legitimate onboarding portal.",
  ],
  LAUNCHPAD_WA_NUMBER: [
    "Sets the WhatsApp destination number encoded into Launchpad handoff links.",
    "It must route customers to the intended production bot rather than an engineering sender.",
  ],
  LIGHT_ANIMATION: [
    "Legacy selector for the provider family used to animate lower-priority or light scenes.",
    "It changes cost and visual behavior across many scenes and is being superseded by explicit registry-backed routing.",
  ],
  LOAD_FILES_CONCURRENCY: [
    "Caps how many source media files the composer downloads or decodes concurrently.",
    "It balances startup latency against memory, network, and file-descriptor pressure in a Batch job.",
  ],
  LOG_LEVEL: [
    "Sets the minimum severity emitted by the service logger.",
    "It controls diagnostic visibility and log volume; overly verbose logs can expose data or inflate cost.",
  ],
  LTX_ASG_NAME: [
    "Names the Auto Scaling Group checked when determining self-hosted LTX worker readiness.",
    "Readiness routing needs the correct fleet identity to avoid sending jobs to an absent worker pool.",
  ],
  LTX_COMFY_MODE: [
    "Selects direct HTTP ComfyUI execution or the SQS/S3 LTX worker-fleet transport.",
    "The modes have different reachability, scaling, and result contracts and cannot be interchanged accidentally.",
  ],
  LTX_QUEUE_URL: [
    "Points the composer at the SQS queue used to submit self-hosted LTX generation jobs.",
    "A wrong queue strands clips or crosses environments; the worker fleet and result bucket must match it.",
  ],
  LTX_WORKER_CONTRACT_VERSION: [
    "Pins the message and result contract expected between the composer and LTX workers.",
    "Version agreement prevents a newer producer from sending fields an older worker cannot interpret.",
  ],
  LTX_WORK_BUCKET: [
    "Names the S3 bucket used to exchange LTX inputs, job metadata, and generated outputs.",
    "The queue-mode provider relies on deterministic object conventions, so bucket drift makes completed work invisible.",
  ],
  MEDIA_BUCKET: [
    "Names the media bucket the mobile BFF uses when signing user-authorized Echo playback URLs.",
    "It must match the dashboard's ownership records and remain environment-local to prevent cross-user or cross-environment playback.",
  ],
  MEDIA_BUCKETS: [
    "Lists the buckets the dashboard API is allowed to use for Echo media operations.",
    "This is an explicit storage allowlist that blocks signing or reading objects from arbitrary buckets.",
  ],
  MEDIA_SIGN_TTL_SEC: [
    "Sets the lifetime of signed media URLs returned by the mobile BFF.",
    "Short-lived URLs limit sharing exposure while still allowing reliable video startup and playback.",
  ],
  META_CAPI_ACCESS_TOKEN: [
    "Authenticates server-side Meta Conversions API event delivery.",
    "It enables consent-aware attribution; exposure permits unauthorized events or access to the connected dataset.",
  ],
  META_CAPI_DATASET_ID: [
    "Identifies the Meta dataset that receives server-side conversion events.",
    "Events sent to the wrong dataset corrupt attribution and reporting across campaigns or environments.",
  ],
  META_CAPI_MILESTONE_EVENT: [
    "Names the Meta conversion event emitted for the configured user milestone.",
    "Stable event naming is required for campaign optimization and comparable funnel reporting.",
  ],
  META_WABA_ID: [
    "Identifies the Meta WhatsApp Business Account used by the bot integration.",
    "It anchors phone numbers, templates, and permissions to the intended business account.",
  ],
  ML_OPTION_BUTTONS_ENABLED: [
    "Renders director interview choices as tappable WhatsApp buttons instead of relying only on typed numeric replies.",
    "It improves completion and reduces invalid answers, but changes the customer-facing interview interaction.",
  ],
  MOTION_FRAMING_ENABLED: [
    "Allows framing plans to account for subject movement observed in generated clips.",
    "It reduces crops that are correct on the first frame but lose a moving subject later.",
  ],
  MOTION_FRAMING_TRACKING_ENABLED: [
    "Enables dynamic crop tracking when both the framing plan and scene policy allow it.",
    "Tracking can preserve moving subjects but may introduce visible camera motion, so it is separately gated.",
  ],
  MUSIC_GENERATION_QC_ENABLED: [
    "Runs technical quality checks on generated music, including duration, silence coverage, and beat stability.",
    "Bad music can break pacing or produce silent sections even when the final video render succeeds.",
  ],
  MUSIC_GENERATION_QC_ENFORCEMENT_MODE: [
    "Selects whether music QC only reports findings or may retry and reject an unusable track.",
    "It controls the cost and availability impact of enforcing audio quality.",
  ],
  OPENAI_API_KEY: [
    "Authenticates OpenAI model calls used by the bot or orchestrator's configured LLM tasks.",
    "Missing credentials break those tasks; exposed credentials permit unauthorized billable API use.",
  ],
  OPENAI_MAX_TOKENS: [
    "Caps the output tokens allowed for the bot's OpenAI/Orson model request.",
    "It bounds latency and spend while ensuring the response has enough room for the intended structured result.",
  ],
  OPENAI_TIMEOUT: [
    "Sets the request timeout for the bot's OpenAI/Orson calls.",
    "It prevents a stalled model call from blocking the WhatsApp conversation indefinitely.",
  ],
  OPTIN_PROMPT_ENABLED: [
    "Shows the WhatsApp marketing opt-in prompt to eligible new users.",
    "It is part of consent capture and must remain aligned with GDPR records and downstream marketing eligibility.",
  ],
  ORSON_GPT_MODEL: [
    "Selects the OpenAI model used by the bot's legacy Orson vision/assistant path.",
    "Model compatibility affects response quality, latency, cost, and whether image inputs are accepted.",
  ],
  OTP_MAX_ATTEMPTS: [
    "Limits incorrect verification attempts for a single mobile login OTP.",
    "It reduces brute-force risk while allowing a reasonable number of user mistakes.",
  ],
  OTP_MAX_SENDS_PER_HOUR: [
    "Caps how many OTP messages the mobile BFF sends to one number per hour.",
    "It limits abuse, Meta messaging cost, and repeated unwanted authentication messages.",
  ],
  OTP_RESEND_COOLDOWN_SEC: [
    "Sets the minimum delay before another OTP can be sent to the same number.",
    "The cooldown prevents rapid resend abuse and avoids confusing users with multiple valid-looking codes.",
  ],
  OTP_STUB: [
    "Enables deterministic test OTP behavior outside production for explicitly allowed numbers.",
    "It supports automated testing but would be an authentication bypass if active for customer-facing users.",
  ],
  OTP_STUB_ALLOWLIST: [
    "Lists the phone numbers permitted to use deterministic stub OTPs in non-production environments.",
    "The allowlist is the final guard that prevents test login behavior from applying broadly.",
  ],
  OTP_STUB_CODE: [
    "Defines the deterministic OTP accepted for allowlisted stub-mode test accounts.",
    "It must never become an active production credential or be accepted outside the stub allowlist.",
  ],
  OTP_TABLE_NAME: [
    "Names the DynamoDB table that stores hashed OTP challenges, attempt counters, and expiry state.",
    "Authentication depends on an environment-local table with TTL and rate-limit state intact.",
  ],
  OTP_TTL_SEC: [
    "Sets how long a mobile login OTP remains valid after it is issued.",
    "A bounded validity window reduces replay risk while giving users time to receive and enter the code.",
  ],
  PAYMENTS_MODE: [
    "Selects whether the bot uses Stripe payments or grants access/tokens through a configured free or development mode.",
    "It directly controls monetization and entitlement, so an incorrect mode can charge users wrongly or bypass payment.",
  ],
  PET_SUBJECT_PROTECTION_ENABLED: [
    "Extends smart-crop detection and protected regions to pet heads and animal subjects.",
    "It prevents cats and dogs from being cropped like background content, while keeping human-only behavior unchanged when off.",
  ],
  PIAPI_API_KEY: [
    "Authenticates PiAPI requests used by the Kling image-to-video adapter and related provider operations.",
    "Missing credentials remove that route; exposure permits unauthorized billable generations.",
  ],
  PLAINLY_API_KEY: [
    "Authenticates Plainly title-card rendering requests.",
    "Title generation fails or falls back without it, while exposure permits unauthorized rendering charges.",
  ],
  PORTRAIT_POSTCARD_ENABLED: [
    "Uses a contain-and-background treatment for portrait stills instead of aggressively cover-cropping them.",
    "It preserves people and composition in vertical photos that would otherwise be visibly clipped.",
  ],
  POSTER_DELIVERY_ENABLED: [
    "Allows the bot to deliver the generated Echo poster to the user.",
    "It separates customer delivery from upstream poster generation so distribution can be stopped without disabling render work.",
  ],
  POSTER_FIRST_FRAME_ENABLED: [
    "Allows the composer to use the generated poster as the video's first-frame treatment where supported.",
    "It affects the opening visual and must remain compatible with aspect ratio and title/poster availability.",
  ],
  POSTER_GENERATION_ENABLED: [
    "Enables orchestrator generation of the poster asset associated with an Echo.",
    "The poster is used for presentation and delivery, but adds model/render work and needs an independent rollback.",
  ],
  POSTGRES_URL: [
    "Provides the orchestrator's PostgreSQL connection string for durable LangGraph checkpoints and workflow state.",
    "Without it, resumability and conversation/workflow continuity fail; it is also a sensitive database credential.",
  ],
  POST_GENERATION_EDITING_ENABLED: [
    "Enables the cross-service post-generation editing and re-prompt workflow for completed Echoes.",
    "It is the single legacy rollout gate for a customer-visible flow spanning the bot, orchestrator, and durable Echo state.",
  ],
  PROTECTED_FALLBACK_CROP_ENABLED: [
    "Uses protected subject regions when the primary crop planner falls back to a simpler crop path.",
    "It prevents a fallback from discarding face protections and reintroducing the very clipping the planner was meant to avoid.",
  ],
  PROVIDER_FRAMING_PLAN_ENABLED: [
    "Applies the shared framing plan to provider input images before image-to-video submission.",
    "It keeps provider generation and final composition aligned on the same protected subject region.",
  ],
  PUBLIC_PHONE_NUMBER: [
    "Sets the public WhatsApp number used in wa.me links and sharing surfaces.",
    "It must point customers at the intended bot number and not an engineering or retired sender.",
  ],
  QC_ENABLED: [
    "Enables the composer's overall audio-visual quality-control pipeline.",
    "It is the master gate for detecting delivery defects before a completed Echo is accepted.",
  ],
  QC_REPAIR_ENABLED: [
    "Allows the general QC pipeline to apply supported automatic repairs after a failure.",
    "Repairs can rescue an Echo but add processing and must remain bounded to avoid loops or hidden quality changes.",
  ],
  QC_REPORT_EVENT_ENABLED: [
    "Publishes the final QC report into dashboard journey events for CRM audit and diagnosis.",
    "It makes quality decisions operator-visible without changing rendered output.",
  ],
  QC_SEMANTIC_ENABLED: [
    "Adds model-based semantic review to the general composer QC pipeline.",
    "It can detect content-level defects that signal processing cannot, with extra latency, cost, and model variance.",
  ],
  REAL_ESTATE_ASSISTED_FALLBACK_ENABLED: [
    "Allows the bot to fall back to an assisted real-estate intake when automatic listing ingestion cannot complete.",
    "It keeps agents moving without silently inventing property data or abandoning the B2B flow.",
  ],
  REAL_ESTATE_EXTRACTION_FALLBACK_MODEL: [
    "Selects the backup model used when the primary real-estate listing extraction model fails.",
    "A compatible fallback improves ingestion availability while preserving structured property output.",
  ],
  REAL_ESTATE_EXTRACTION_MODEL: [
    "Selects the primary model that extracts structured property facts from real-estate content.",
    "Extraction accuracy drives factual video copy, image selection, and agent trust.",
  ],
  REAL_ESTATE_GENERIC_INGEST_ENABLED: [
    "Enables generic listing-content ingestion beyond the narrow URL-specific path.",
    "It broadens supported agent inputs and therefore needs careful validation against SSRF, size, and factuality controls.",
  ],
  REAL_ESTATE_INGEST_EVENTS_ENABLED: [
    "Emits audit events for real-estate ingestion attempts, decisions, fallbacks, and outcomes.",
    "It makes B2B intake failures diagnosable without relying on raw customer messages.",
  ],
  REAL_ESTATE_RECIPES_ENABLED: [
    "Enables deterministic real-estate video recipes and skill-specific directing behavior.",
    "Recipes preserve repeatable brand and property-story structure for agency videos.",
  ],
  REAL_ESTATE_URL_INGEST_ENABLED: [
    "Allows eligible users to submit a property URL for automatic page and image ingestion.",
    "It is a powerful convenience path with SSRF, content-size, and factual-extraction risks that need a kill switch.",
  ],
  REDIS_URL: [
    "Provides the bot's Redis connection string for conversation and transient state.",
    "State continuity and concurrency controls depend on it; the URL commonly contains sensitive credentials.",
  ],
  REFRESH_TTL_SEC: [
    "Sets how long a mobile refresh token can be used to obtain new access tokens.",
    "It defines the maximum session lifetime and the exposure window if a refresh token is stolen.",
  ],
  REMAKE_ADMIN_OPERATORS: [
    "Lists operator identities allowed to trigger privileged Echo remake actions.",
    "Remakes consume media and provider capacity, so authorization must be explicit and auditable.",
  ],
  REMAKE_MEDIA_BUCKET: [
    "Names the bucket used by the remake result handler to locate and publish regenerated media.",
    "It must match the environment's media plane so remake results attach to the correct Echo.",
  ],
  REMAKE_RESULTS_QUEUE_URL: [
    "Points the dashboard remake flow at the queue carrying completed remake results.",
    "Incorrect routing leaves operator actions stuck or applies a result in the wrong environment.",
  ],
  RETURN_FORMAT: [
    "Selects whether the composer publishes its completion result to a queue or only writes it to logs.",
    "Normal delivery requires the queue path; log-only mode is useful for tests but can strand a customer Echo.",
  ],
  RUNTIME_CONFIG_CONTROL_PLANE_URL: [
    "Points a service at its environment-local RDS-backed Config Control API.",
    "It is the desired-state authority boundary and must never resolve across production (`stg`) and engineering staging (`staging`).",
  ],
  RUNTIME_CONFIG_SIGNING_KEY_ID: [
    "Identifies the trusted signing key a service uses when validating runtime-configuration snapshots.",
    "Key agreement prevents tampered, unsigned, or wrong-environment configuration from becoming effective.",
  ],
  RUNTIME_CONFIG_SNAPSHOT_BUCKET: [
    "Names the S3 bucket from which a runtime service reads immutable signed configuration snapshots.",
    "It must match the environment-local control plane so services apply only approved releases.",
  ],
  RUNTIME_CONFIG_SNAPSHOT_PREFIX: [
    "Scopes the runtime snapshot path to the literal environment and release namespace.",
    "The prefix is part of the isolation guard against consuming another environment's configuration.",
  ],
  S3_BUCKET_NAME: [
    "Names the primary S3 bucket a pipeline service uses for source media, scripts, or outputs.",
    "Bucket identity is a data-boundary decision; a mismatch breaks media lookup or crosses environments.",
  ],
  S3_BUCKET_PREFIX: [
    "Scopes a service's object keys within its configured S3 bucket.",
    "Stable prefixes keep workloads organized and limit collisions or unintended access to unrelated objects.",
  ],
  SCREENSHOT_INGEST_TRIM_ENABLED: [
    "Lets the orchestrator detect and trim screenshot UI chrome or borders before downstream planning.",
    "Cleaner source images improve analysis and framing, while the original remains available for audit and rollback.",
  ],
  SCREENSHOT_TRIM_ENABLED: [
    "Lets the composer deterministically crop black borders or letterboxing from still images at load time.",
    "It prevents visible bars and misleading crop geometry without invoking a model.",
  ],
  SEGMENT_RENDER_BOUNDARY_QC_ENABLED: [
    "Checks segment-render concatenation for boundary flashbacks and falls back to the single-process render when needed.",
    "It makes parallel rendering self-healing before the final authoritative boundary scan.",
  ],
  SEGMENT_RENDER_ENABLED: [
    "Enables parallel segment-based final rendering instead of a single full-video encode.",
    "It can reduce render time but introduces concat and boundary complexity, so a rollback remains essential.",
  ],
  SEGMENT_RENDER_MAX_SEGMENTS: [
    "Caps how many parallel render segments may be created for one Echo.",
    "The cap prevents CPU oversubscription and excessive temporary files while preserving useful parallelism.",
  ],
  SEGMENT_RENDER_STRATEGY: [
    "Selects the segment rendering algorithm, such as full rebuild or windowed rendering.",
    "Strategies differ in performance and seam risk and must match the deployed compatibility tests.",
  ],
  SEGMENT_RENDER_WINDOWED_BOUNDARY_MODE: [
    "Chooses how windowed rendering places segment boundaries relative to safe scene or frame positions.",
    "Boundary placement is the main defense against flashbacks, duplicate frames, and visible cuts.",
  ],
  SEGMENT_RENDER_WINDOWED_EXTRA_PAD_FRAMES: [
    "Adds overlap/padding frames around windowed segment boundaries before the final trim and concat.",
    "Small padding protects transition context; too much can duplicate content or increase work.",
  ],
  SEGMENT_RENDER_WINDOWED_UNSUPPORTED_POLICY: [
    "Defines whether unsupported windowed-render cases fall back to a safe renderer or fail the job.",
    "It controls availability when an Echo contains effects the optimized path cannot reproduce correctly.",
  ],
  SENTRY_DEBUG: [
    "Enables verbose internal diagnostics from the Sentry SDK.",
    "It helps integration debugging but can create noisy logs and should not remain on during normal operation.",
  ],
  SENTRY_DSN: [
    "Identifies and authenticates the Sentry project that receives service errors and telemetry.",
    "A missing DSN disables reporting; an exposed DSN can permit telemetry spam even though it is not a general account credential.",
  ],
  SENTRY_ENABLED: [
    "Enables Sentry error monitoring for the service when a DSN is also configured.",
    "It provides incident visibility and a quick way to stop telemetry if privacy or volume problems appear.",
  ],
  SENTRY_ENABLE_LOGS: [
    "Forwards selected application logs into Sentry in addition to captured errors.",
    "It improves incident context but increases event volume and the chance of sending unnecessary data.",
  ],
  SENTRY_ENVIRONMENT: [
    "Tags Sentry events with the deployment environment shown in issue grouping and filters.",
    "Correct tags prevent production (`stg`) incidents from being confused with engineering staging (`staging`).",
  ],
  SENTRY_ERROR_SAMPLE_RATE: [
    "Sets the fraction of error events retained by Sentry.",
    "Sampling controls observability cost and volume; too low a rate can hide rare customer failures.",
  ],
  SENTRY_INCLUDE_PHONE_NUMBER: [
    "Allows phone-derived user context to be attached to Sentry events according to the configured PII mode.",
    "Phone data is personal data, so this must remain deliberate, minimized, and aligned with GDPR policy.",
  ],
  SENTRY_PHONE_PII_MODE: [
    "Selects whether phone numbers are omitted, hashed, or sent in full to Sentry.",
    "This is a direct privacy boundary; hashed or off modes reduce exposure while preserving some correlation.",
  ],
  SENTRY_PROFILES_SAMPLE_RATE: [
    "Sets the fraction of transactions that collect Sentry performance profiles.",
    "Profiles help diagnose CPU hotspots but add overhead, data volume, and potential context exposure.",
  ],
  SENTRY_SEND_DEFAULT_PII: [
    "Allows the Sentry SDK's default personally identifiable context collection.",
    "It has GDPR implications and should stay off unless the exact data set and lawful purpose are approved.",
  ],
  SENTRY_TRACES_SAMPLE_RATE: [
    "Sets the fraction of requests or transactions captured as Sentry performance traces.",
    "It balances latency visibility against telemetry cost, overhead, and data minimization.",
  ],
  SERVER_HOST: [
    "Sets the network interface address on which the bot web server listens.",
    "The bind address must match the container/network model without unintentionally exposing a local-only service.",
  ],
  SERVER_PORT: [
    "Sets the port on which the bot web server accepts traffic when the platform PORT value does not override it.",
    "It must agree with the container and load-balancer wiring for health checks and webhooks to reach the service.",
  ],
  SERVER_URL: [
    "Sets the bot's public base URL used for webhook registration and absolute callback links.",
    "A wrong URL prevents Meta callbacks or sends users and integrations to another environment.",
  ],
  SERVICE_RELEASE_SHA: [
    "Records the source revision deployed in the running service.",
    "It lets operators correlate effective configuration, events, and incidents with the exact code release.",
  ],
  SESSIONS_TABLE_NAME: [
    "Names the DynamoDB table containing the dashboard's per-Echo session projection.",
    "The CRM depends on this environment-local projection for current journey state, media, costs, and operator views.",
  ],
  SHORT_VIDEO_ALERTS_ENABLED: [
    "Enables Slack alerts when a delivered Echo is shorter than the configured minimum duration.",
    "It provides a fast operational signal for truncated or prematurely completed renders.",
  ],
  SHORT_VIDEO_FFPROBE_TIMEOUT_SECONDS: [
    "Sets the maximum time allowed for ffprobe while measuring a final video's duration for alerting.",
    "It prevents a corrupt or remote file from blocking completion solely for a diagnostic check.",
  ],
  SHORT_VIDEO_MIN_DURATION_SECONDS: [
    "Defines the duration below which the composer classifies a final video as suspiciously short.",
    "The threshold determines alert sensitivity and should reflect intended Echo lengths to avoid noise or missed truncation.",
  ],
  SLACK_ALERT_TIMEOUT_SECONDS: [
    "Sets the network timeout for posting composer alerts to Slack.",
    "Alert delivery must fail quickly and never hold up customer video completion.",
  ],
  SLACK_RATINGS_WEBHOOK_URL: [
    "Provides the private Slack webhook used by the dashboard indexer to notify operators about ratings or feedback events.",
    "Exposure permits message injection, while a missing webhook removes a useful but non-authoritative alert channel.",
  ],
  SLACK_WEBHOOK_URL: [
    "Provides the private Slack webhook used for composer quality and short-video alerts.",
    "It is an operator notification credential; alert failure must not become a render failure.",
  ],
  SMTP_HOST: [
    "Names the SMTP server used by the optional bot email-delivery path.",
    "It determines where outbound mail is submitted and must match the authenticated sender configuration.",
  ],
  SMTP_PASSWORD: [
    "Authenticates the bot to the configured SMTP server.",
    "Exposure permits unauthorized mail sending; an incorrect value breaks email delivery.",
  ],
  SMTP_PORT: [
    "Sets the network port used to connect to the SMTP server.",
    "It must match the provider's TLS mode and endpoint for reliable secure delivery.",
  ],
  SMTP_USERNAME: [
    "Provides the account name used to authenticate to the SMTP server.",
    "It must correspond to the password and authorized sender identity.",
  ],
  SMTP_USE_TLS: [
    "Controls whether the bot upgrades the SMTP connection with TLS.",
    "TLS protects credentials and message content in transit and should only be disabled for a controlled local server.",
  ],
  SNAP_ENABLED: [
    "Enables the Instax Snap QR campaign intake and Snap-specific WhatsApp journey.",
    "It is the master product gate for a distinct acquisition and media-seeding flow.",
  ],
  SNAP_PIN_OPENING_IMAGE: [
    "For Snap-seeded Echoes, instructs the orchestrator to keep the scanned opening image as the first story image.",
    "It preserves the physical-to-digital reveal promised by the QR experience without affecting non-Snap Echoes.",
  ],
  SNAP_PORTAL_API_KEY: [
    "Authenticates trusted Snap portal requests to the bot's campaign/intake API.",
    "It prevents unauthorized creation or mutation of Snap claims and uploaded media.",
  ],
  SNAP_PORTAL_ORIGIN: [
    "Sets the trusted public origin of the Snap portal used in links and request validation.",
    "It keeps users and cross-origin checks pointed at the legitimate campaign portal.",
  ],
  SNAP_UPLOAD_PREFIX: [
    "Scopes Snap-uploaded media objects under a dedicated storage prefix.",
    "A stable prefix supports ownership checks, retention, and separation from ordinary WhatsApp uploads.",
  ],
  SNAP_WA_NUMBER: [
    "Sets the WhatsApp destination number encoded into Snap QR handoff links.",
    "It must route customers to the intended production bot rather than an engineering sender.",
  ],
  SOUND_AUDITION_BUCKET_NAME: [
    "Names the private bucket holding CRM sound audition inputs and rendered previews.",
    "It isolates operator experiments and supports controlled signing and cleanup of temporary audio artifacts.",
  ],
  SOUND_EVIDENCE_TABLE_NAME: [
    "Names the table storing sound decisions, evidence, and audition audit records.",
    "It makes audio choices reproducible and gives operators provenance beyond the final mixed file.",
  ],
  STATE_MACHINE_ARN: [
    "Identifies the Step Functions state machine invoked by the video-job dispatcher.",
    "It is the execution entry point for composer work and must be environment-local and revision-compatible.",
  ],
  STORAGE_TYPE: [
    "Selects the service's media storage adapter, such as local filesystem or S3.",
    "It changes where customer media is written and read and therefore affects durability and data boundaries.",
  ],
  STRIPE_PUBLISHABLE_KEY: [
    "Identifies the Stripe account for client-safe payment initialization.",
    "It must match the secret key's account and environment so payment UI and server charges agree.",
  ],
  STRIPE_SECRET_KEY: [
    "Authenticates privileged Stripe API calls for payment and entitlement operations.",
    "Exposure can compromise billing; using the wrong environment can create real or unusable charges.",
  ],
  STRIPE_WEBHOOK_SECRET: [
    "Verifies that incoming Stripe webhook payloads were signed by the configured endpoint.",
    "Without correct verification, forged payment events could grant entitlement or corrupt billing state.",
  ],
  SUBJECT_GROUNDING_ENABLED: [
    "Allows the framing pipeline to call the controlled subject-grounding service for named non-face subjects.",
    "It improves object-aware crops but introduces an external inference call with privacy, latency, and licensing controls.",
  ],
  SUBJECT_GROUNDING_MAX_CONCURRENCY: [
    "Caps concurrent calls to the subject-grounding service within a composer job.",
    "It bounds service load, latency spikes, and the amount of media processed at once.",
  ],
  SUBJECT_GROUNDING_MODEL: [
    "Pins the detector/model identity requested from the subject-grounding service.",
    "Model identity affects box semantics, licensing, reproducibility, and compatibility with stored framing evidence.",
  ],
  SUPABASE_SERVICE_ROLE_KEY: [
    "Grants server-side administrative access to the configured Supabase project for trusted bot or service operations.",
    "It bypasses normal row-level restrictions and must never reach the browser or logs.",
  ],
  SUPABASE_URL: [
    "Identifies the Supabase project used by the service for legacy CRM/cohort or trusted metadata access.",
    "It must match the service-role credential and correct environment; Supabase is not the new runtime-config authority.",
  ],
  TITLE_VISUAL_QC_ENABLED: [
    "Uses a vision model to inspect rendered title cards and retry with shorter copy when text breaches safe margins.",
    "It catches visible title overflow that text-only validation cannot see.",
  ],
  TITLE_VISUAL_QC_LLM: [
    "Selects the model used to judge and rewrite title cards during visual QC.",
    "Model capability and stability affect both detection accuracy and the wording of repaired titles.",
  ],
  TITLE_VISUAL_QC_MAX_ATTEMPTS: [
    "Caps how many visual-QC rewrite and re-render attempts a title card may receive.",
    "It bounds model/render cost and prevents a bad title from causing an unending repair loop.",
  ],
  TOKENS_PER_DOLLAR: [
    "Defines the legacy conversion between purchased dollars and in-product generation tokens.",
    "It directly affects customer entitlement and revenue accounting.",
  ],
  USE_FALLBACK_MODEL_FOR_SCRIPT: [
    "Forces script generation onto the orchestrator's fallback model path instead of the primary GPT route.",
    "It is an incident/availability control that can change script style, quality, latency, and cost.",
  ],
  VEO_API_BACKEND: [
    "Selects whether Veo requests use the Gemini Developer API or Vertex AI backend.",
    "The backends use different credentials, model IDs, regions, quotas, and policy controls.",
  ],
  VEO_FAST_MODEL: [
    "Pins the Veo fast model used by the canonical lower-cost/faster provider route.",
    "It determines the exact capability, price, and output behavior of high-volume secondary scenes.",
  ],
  VEO_LITE_MODEL: [
    "Pins the independent Veo 3.1 Lite Preview model used when the `veo_gemini_lite` route is selected.",
    "It controls the exact capability, lifecycle, price, and provenance of Lite generations and must never be treated as a Fast-model alias.",
  ],
  VEO_MODEL: [
    "Pins the standard Veo model used by the primary `veo_gemini` image-to-video route.",
    "The model ID controls quality, supported options, latency, and billable price.",
  ],
  VEO_PERSON_GENERATION: [
    "Sets Vertex Veo's policy for generating people from source images, such as allowing adult subjects.",
    "Echoflicks videos commonly contain people, so an incompatible policy can reject most customer scenes.",
  ],
  VEO_VERTEX_CREDENTIALS_FILE: [
    "Points the AWS-hosted composer at a Workload Identity Federation credential configuration for Vertex Veo.",
    "It enables keyless cross-cloud authentication and must reference the intended project and trust configuration.",
  ],
  VEO_VERTEX_LOCATION: [
    "Selects the Vertex AI region used for Veo publisher-model requests.",
    "Model availability, data location, quota, and latency are regional.",
  ],
  VIDEO_COMPOSING_QUEUE_URL: [
    "Points a pipeline service at the queue used for composer-stage work or notifications.",
    "Queue identity determines where the render handoff lands and must remain environment-local.",
  ],
  VIDEO_GENERATION_COST: [
    "Sets the legacy token amount charged for creating one Echo.",
    "It directly controls entitlement consumption and must agree with the configured payments mode.",
  ],
  VIDEO_GENERATION_DLQ_URL: [
    "Points at the dead-letter queue that receives generation messages after repeated processing failures.",
    "The DLQ preserves failed work for audit and recovery instead of silently dropping it.",
  ],
  VIDEO_GENERATION_OUTPUT_QUEUE_URL: [
    "Points the composer at the queue where it publishes completed or failed generation results.",
    "The bot and dashboard rely on this handoff to update status and deliver the finished Echo.",
  ],
  VIDEO_GENERATION_QUEUE_URL: [
    "Points the bot or orchestrator at the queue carrying new Echo generation work.",
    "It is the primary pipeline handoff; incorrect routing can lose work or cross environments.",
  ],
  VIDEO_RESULTS_QUEUE_URL: [
    "Points a service at the queue carrying final video results back toward the customer flow.",
    "Result delivery and state transitions depend on consuming the same environment-local queue.",
  ],
  VIDEO_STATUS_UPDATES_QUEUE_URL: [
    "Points at the queue used to publish and consume intermediate Echo status updates.",
    "It keeps WhatsApp and CRM progress aligned with the actual pipeline journey.",
  ],
  WAN_API_KEY: [
    "Authenticates requests to the self-hosted WAN/Comfy image-to-video service.",
    "Exposure permits unauthorized use of private generation capacity; a mismatch removes that provider route.",
  ],
  WAN_API_URL: [
    "Sets the base URL of the self-hosted WAN/Comfy image-to-video service.",
    "It determines where source media and generation requests are sent and must point at the intended trusted worker.",
  ],
  WHATSAPP_ACCESS_TOKEN: [
    "Authenticates Meta Graph API calls that send and manage WhatsApp messages.",
    "Exposure can compromise customer messaging; an invalid token stops bot replies, templates, and OTP delivery.",
  ],
  WHATSAPP_ACCESS_TOKEN_SECRET_ARN: [
    "Points server-side dashboard or mobile services at the Secrets Manager object containing the WhatsApp access token.",
    "Using a secret reference keeps the bearer token out of Terraform state, browser code, and plain environment output.",
  ],
  WHATSAPP_ACCESS_TOKEN_SECRET_JSON_KEY: [
    "Selects the JSON field containing the WhatsApp token inside the referenced secret.",
    "It lets several credentials share a managed secret while ensuring the service reads the intended token.",
  ],
  WHATSAPP_APP_ID: [
    "Identifies the Meta application used for dashboard-originated WhatsApp operations.",
    "It must align with the business account, phone number, templates, and token permissions.",
  ],
  WHATSAPP_BROADCAST_ANALYTICS_ENABLED: [
    "Enables broadcast send, delivery, reply, and attribution event projection across dashboard services.",
    "It provides campaign performance evidence but must agree across API, worker, and indexer to avoid partial analytics.",
  ],
  WHATSAPP_BROADCAST_ATTRIBUTION_WINDOW_HOURS: [
    "Sets how long after a broadcast a qualifying user action may be attributed to that send.",
    "A consistent window across API and indexer is required for honest conversion reporting.",
  ],
  WHATSAPP_BROADCAST_CLAIM_STALE_SECONDS: [
    "Defines when an unfinished broadcast-send claim is considered stale and eligible for recovery.",
    "It prevents permanent stuck sends while avoiding duplicate delivery from reclaiming healthy work too early.",
  ],
  WHATSAPP_BROADCAST_INLINE_SEND_LIMIT: [
    "Caps the audience size the API may send synchronously before dispatching work to the broadcast worker.",
    "It protects request latency and reliability while keeping very small sends responsive.",
  ],
  WHATSAPP_BROADCAST_QUEUE_URL: [
    "Points the dashboard API and worker at the SQS queue carrying asynchronous broadcast jobs.",
    "The queue decouples large sends and must be shared by the matching environment's producer and consumer.",
  ],
  WHATSAPP_BUSINESS_ACCOUNT_ID: [
    "Identifies the WhatsApp Business Account used for templates, broadcasts, and message analytics.",
    "It must match the phone number and access token or Meta operations fail or target the wrong account.",
  ],
  WHATSAPP_DOCUMENT_VIDEO_MAX_BYTES: [
    "Sets the largest video file the bot will attempt to deliver as a WhatsApp document fallback.",
    "It bounds Graph API failures and determines when the pipeline must use another delivery strategy.",
  ],
  WHATSAPP_DOCUMENT_VIDEO_MIME_TYPE: [
    "Sets the MIME type declared when a large Echo is uploaded and sent as a WhatsApp document.",
    "Correct media typing lets clients recognize and play the file reliably.",
  ],
  WHATSAPP_FREEFORM_SAFETY_BUFFER_SECONDS: [
    "Reserves time inside Meta's 24-hour customer-service window before allowing a free-form outbound message.",
    "The buffer reduces policy failures caused by clock skew or messages sent just after the window closes.",
  ],
  WHATSAPP_GRAPH_API_VERSION: [
    "Pins the Meta Graph API version used for WhatsApp requests.",
    "Version pinning prevents unreviewed API behavior changes and gives a controlled upgrade path.",
  ],
  WHATSAPP_LARGE_VIDEO_DELIVERY_MODE: [
    "Selects how the bot handles Echoes too large for normal native-video delivery, such as automatic document fallback.",
    "It determines whether customers receive a playable video, document, or alternate link when size limits are exceeded.",
  ],
  WHATSAPP_LARGE_VIDEO_DOCUMENT_ENABLED: [
    "Allows oversized Echoes to be sent as WhatsApp documents when native-video delivery is unsuitable.",
    "It preserves delivery availability while avoiding repeated failures against native media limits.",
  ],
  WHATSAPP_MARKETING_US_BLOCK_ENABLED: [
    "Blocks configured marketing-template sends to US recipients where policy or campaign rules require it.",
    "It is a compliance guard that must agree between audience preview and the asynchronous sender.",
  ],
  WHATSAPP_OTP_TEMPLATE: [
    "Names the approved WhatsApp authentication template used to deliver mobile login codes.",
    "OTP delivery outside an open conversation requires an approved authentication template with the expected parameters.",
  ],
  WHATSAPP_OTP_TEMPLATE_LANG: [
    "Selects the language/locale of the WhatsApp OTP template.",
    "It must match an approved template translation or Meta rejects the authentication message.",
  ],
  WHATSAPP_PHONE_NUMBER_ID: [
    "Identifies the Meta WhatsApp sender number used for Graph API messaging.",
    "It determines which bot identity contacts the user and must match the token and business account.",
  ],
  WHATSAPP_TEMPLATE_ALLOWLIST: [
    "Restricts CRM broadcasts to approved WhatsApp template names.",
    "It prevents operators or compromised clients from invoking unreviewed templates or message purposes.",
  ],
  WHATSAPP_TEMPLATE_MEDIA_MAX_BYTES: [
    "Caps media uploaded for use in WhatsApp template headers.",
    "It enforces Meta/provider limits before send time and protects memory and storage usage.",
  ],
  WHATSAPP_TEMPLATE_MEDIA_PREFIX: [
    "Scopes uploaded WhatsApp template media to an approved object-key prefix.",
    "It supports storage authorization, lifecycle cleanup, and prevents arbitrary media keys from becoming broadcast assets.",
  ],
  WHATSAPP_VERIFY_TOKEN: [
    "Verifies Meta's initial WhatsApp webhook subscription challenge.",
    "Only the matching private token should authorize webhook registration for the bot endpoint.",
  ],
  WORKING_SOURCE_ENABLED: [
    "Creates normalized per-scene working-source media before analysis, framing, and generation.",
    "It gives downstream stages a stable, auditable source while preserving the original upload for rollback and evidence.",
  ],
};

export function hasDocumentedRuntimeVariable(name: string): boolean {
  return Object.hasOwn(METADATA_BY_NAME, name);
}

export function getRuntimeVariableMetadata(
  name: string,
): RuntimeVariableMetadata {
  const copy = METADATA_BY_NAME[name];
  if (copy) {
    return { purpose: copy[0], importance: copy[1], documented: true };
  }

  return {
    purpose: `${name} is reported by the owning service as part of its live runtime inventory.`,
    importance:
      "This key has not yet been added to the CRM documentation catalog. Verify its owning code path and environment boundary before changing it.",
    documented: false,
  };
}
